import {PHPErrorResponse, PHPListResponse} from '@/api/channel/type';
import {UCC_FILE_TYPE} from '@/constants/channel';
import {
  BoardType,
  PostDetailSchema,
} from '@/types/channel/post/detail/detail.schema';

/**
 * 게시글 리스트 응답 스키마
 */
export interface PostListSchema extends PostDetailSchema {
  // board에서만 필요한 필드들 추가
  vote: PostListVoteSchema | null;
  reserve: PostListReserveSchema | null;
  authority: PostListAuthoritySchema;
}

/**
 * 썸네일 표시 타입 (스트리머에게만 노출)
 */
export type ThumbTypeForStreamer =
  | 'AUTH_DENIED'
  | 'EXPIRE' // @deprecated
  | 'ENCODING_FAIL' // 인코딩 실패
  | 'UPLOAD_FAIL' // 업로드 실패
  | 'SAVYG_MAKING' // 사빅 제작 중
  | 'DELETE' // 숲지기가 삭제
  | 'LOADING'; // 인코딩 중

/**
 * 썸네일 표시 타입 (유저에게 노출)
 */
export type ThumbTypeForUser =
  | 'SUCCEED' // 정상 노출
  | 'BLIND' // 블라인드
  /* Note. 아래 3개 타입은 25년 9월 중 수정 작업 예정되어있습니다. */
  | 'ADULT_LOCK' // 연령제한 + 비번방
  | 'LOCK' // 비번방
  | 'ADULT'; // 연령제한

export type PostListUccThumbType = ThumbTypeForStreamer | ThumbTypeForUser;

export type PostListUccFileType =
  (typeof UCC_FILE_TYPE)[keyof typeof UCC_FILE_TYPE];

export type PostListUccFlag =
  | 'SUCCEED'
  | 'ENCODING'
  | 'ENCODING_WAIT'
  | 'ENCODING_FAIL'
  | 'UPLOADING'
  | 'UPLOAD_FAIL'
  | 'ADMIN_DELETE'
  | 'FILTERING'
  | 'TIMEOUT_DELETE'
  | 'BJ_HIDDEN'
  | 'LIST_HIDDEN';

export interface PostListUccSchema {
  thumb: string;
  thumbType: PostListUccThumbType;
  flag: PostListUccFlag;
  uccType: string;
  category: number;
  grade: number;
  totalFileDuration: number;
  fileType: PostListUccFileType;
  autoDeleteRemainHours: number | null;
  storyIdx: number | null;
  paidPpv: number | null;
  vodCategory?: string;
}

export interface PostListResponse extends PHPListResponse<PostListSchema> {
  noticeData: PostListSchema[];
}

/**
 * 게시글 리스트 투표 정보 스키마
 */
export interface PostListVoteSchema {
  id: number;
  userId: string;
  type: number;
  answerCount: number;
  url: string;
  question: string;
  createdAt: {
    date: string;
    timezoneType: number;
    timezone: string;
  };
  isEnd: boolean;
  endAt: string;
  status: number; // TODO: php 타입 확인 후 union 처리
}

/**
 * 게시글 리스트 뱃지 정보 스키마
 */
export interface PostListBadgeSchema {
  userId: string;
  bjId: string;
  isManager: 0 | 1;
  isTopFan: 0 | 1;
  isFan: 0 | 1;
  isSubscribe: 0 | 1;
  isSupport: 0 | 1;
}

/**
 * 게시글 리스트 권한 정보 스키마
 */
export interface PostListAuthoritySchema {
  isBoardShareable: boolean;
  isBoardDeletable: boolean;
  isReasonDeletable: boolean;
  isUccDeletable: boolean;
  isBoardUpdatable: boolean;
  isUccUpdatable: boolean;
  isMovable: boolean;
  isPinable: boolean;
  isPinFixable: boolean;
  isPinCancelable: boolean;
  isVodPinable: boolean;
  isVodPinFixable: boolean;
  isVodPinCancelable: boolean;
  isNoticeCancelable: boolean;
  isCommentWritable: boolean;
  isReplyWritable: boolean;
  isReportable: boolean;
  isUccEditable: boolean;
  isUccMidRollSettable: boolean;
}

/**
 * 게시글 리스트 예약 정보 스키마
 */
export interface PostListReserveSchema {
  autoDeleteDate: string;
}

/**
 * 게시글 리스트 요청 파라미터
 */
export interface PostListQueryParams {
  userId: string;
  page?: number;
  perPage?: number;
  startDate?: string; // YYYYMMDD
  endDate?: string;
  field?: PostListField;
  type: PostListType;
  keyword?: string;
  orderBy?: PostListOrderby;
  boardNumber?: string;
}

export const POST_LIST_FIELD = 'title,contents,user_nick,user_id,hashtags';
export type PostListField = typeof POST_LIST_FIELD;

/**
 * 게시글 좋아요 요청
 */
export interface LikePostParams {
  userId: string;
  postId: string;
  boardType?: 104 | 105;
}

/**
 * 게시글 좋아요 응답
 */
export interface LikePostResponse {
  message: string;
  titleNo: number;
  userId: string;
}

/**
 * 게시글 다중 삭제 요청
 */
export interface DeletePostListParams {
  userId: string;
  postIds: number[];
}

/**
 * 게시글 삭제 요청
 */
export interface DeletePostParams {
  userId: string;
  postId: number;
}

/**
 * 공지 해제 요청
 */
export interface CancelNoticeParams {
  userId: string;
  postId: number;
}

/**
 * 홈 고정 설정 요청
 */
export interface SetFixToHomeParams {
  userId: string;
  titleNo: number;
  /** YYYY-MM-DD HH:MM 혹은 빈 문자열 */
  expireDate: string;
  _token: string;
}

/**
 * 홈 고정 가능 여부 조회 요청
 */
export interface GetFixToHomeParams {
  userId: string;
  titleNo: number;
}

/**
 * 홈 고정 가능 여부 조회 응답
 */
export interface GetFixToHomeResponse {
  isPin?: number;
  message: string;
}

export type PostListOrderby = 'reg_date' | 'read_cnt';
export type PostListType = 'all' | 'post' | 'vod';

export interface GetPostMoveParams {
  userId: string;
  postId: number[]; // 다중 선택 들어갈 경우 Array
  oldBbsId: number;
  newBbsId: number;
  bbsName?: string;
}

export interface GetPostMoveResponse {
  message: string;
  oldBbsNo?: number;
  titleNo?: number[];
}

export interface GetReportListParams {
  userId: string;
  isStationReport: boolean; // 방송국 자체 신고인지 여부
}

export type GetReportListResponse = ReportSchema[];

export interface ReportSchema {
  reportIdx: number;
  reportInfoNo: number;
  sdate: string; // yyyy-mm-dd hh:mm:ss
  edate: string;
  comment: string;
  locale: string;
  category: number;
  regDate: string; // yyyy-mm-dd hh:mm:ss
}

export interface ReportPostParamsSchema {
  userId: string;
  writerId: string;
  commonIdx: number; // 게시글 번호
  boardType: BoardType;
  mode: 'layer' | 'page'; // 모바일이면 page, PC면 layer 인듯;;
  isStationReport: boolean; // 방송국 자체 신고인지 여부 (모바일이면 항상 true 인듯;;)
  reportInfoNo: number; // 신고 카테고리 번호
  _token?: string;
  comment?: string;
}

export interface ReportPostResponse {
  stationNo: number;
  commonIdx: string;
  boardType: string;
  reportInfoNo: string;
  userNick: string;
  userId: string;
  comment: null; // TODO: 정확한 값 확인 필요
  regDate: {}; // TODO: 정확한 값 확인 필요
  reportNo: number;
}

export interface ReportPostErrorResponse {
  code: number;
  message: string;
  line: number;
  file: string;
}

/**
 * 홈 고정 해제 시 요청 파라미터
 */
export interface DeletePinParamsSchema {
  userId: string;
  postId: number;
}

export interface DeleteLikePostParams {
  userId: string;
  postId: number;
}

export const BOARD_LIST_ERROR = {
  NOT_LOGIN: 1100,
  NOT_FAVORITE: 1103,
  NOT_FANCLUB: 1104,
  NOT_MANAGER: 1106,
  NOT_SUBSCRIPTION: 1107,
  PRIVATE: 1102,
} as const;

export type BoardListError =
  (typeof BOARD_LIST_ERROR)[keyof typeof BOARD_LIST_ERROR];

export interface BoardListErrorResponse
  extends PHPErrorResponse<BoardListError> {}

export interface AddFavoriteParamsSchema {
  bjId: string;
}

export interface AddFavoriteResponse {
  bjId: string;
  message: string;
}

export interface BlindPostParamsSchema {
  userId: string;
  postId: number;
  postUserId: string;
}

export interface BlindPostResponseSchema {
  message: string;
}
